import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sortVariants } from '../lib/sortVariants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

const RETURN_REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'excess', label: 'Excess Quantity' },
  { value: 'damaged', label: 'Damaged in Transit' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'not_moving', label: 'Not Moving' },
  { value: 'other', label: 'Other' },
];

const SalesReturnManagement = ({ user }) => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const bulk = useBulkSelect();
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingReturn, setViewingReturn] = useState(null);
  const [expandedSR, setExpandedSR] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterDistributors, setFilterDistributors] = useState([]);
  const [filterStaff, setFilterStaff] = useState([]);
  const [billingDialog, setBillingDialog] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canCreate = ['super_admin', 'admin', 'manager', 'inventory_staff', 'salesman'].includes(user?.role);
  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const canChangeStatus = ['super_admin', 'admin', 'manager', 'inventory_staff'].includes(user?.role);

  const updateTallyStatus = async (returnId, newStatus, creditNoteNumber) => {
    try {
      const payload = { tally_status: newStatus };
      if (creditNoteNumber) payload.credit_note_number = creditNoteNumber;
      await api.put(`/sales-returns/${returnId}/tally-status`, payload);
      toast.success(`Status updated to ${newStatus === 'billed' ? 'Billed' : 'Pending'}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleStatusChange = (returnId, newStatus) => {
    if (newStatus === 'billed') {
      setBillingDialog({ entryId: returnId, inputValue: '' });
    } else {
      updateTallyStatus(returnId, newStatus);
    }
  };

  const confirmBilling = () => {
    if (!billingDialog) return;
    updateTallyStatus(billingDialog.entryId, 'billed', billingDialog.inputValue || null);
    setBillingDialog(null);
  };

  const loadData = async () => {
    try {
      const [returnsRes, customersRes, agentsRes, distRes] = await Promise.allSettled([
        api.get('/sales-returns'), api.get('/customers?include_inactive=true'), api.get('/agents'), api.get('/distributors'),
      ]);
      if (returnsRes.status === 'fulfilled') setReturns(returnsRes.value.data);
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data);
      if (agentsRes.status === 'fulfilled') setAgents(agentsRes.value.data);
      if (distRes.status === 'fulfilled') setDistributors(distRes.value.data);
    } catch (err) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const customerMap = Object.fromEntries(customers.map(c => [c.id, c]));
  const getCustDisplay = (id) => {
    const c = customerMap[id];
    if (!c) return id;
    const name = c.company_name || id;
    const loc = c.city?.toLowerCase() === 'mumbai' ? c.area : c.city;
    return loc ? `${name} (${loc})` : name;
  };
  const getCustAgent = (id) => { const c = customerMap[id]; return c?.agent_id ? agents.find(a => a.id === c.agent_id)?.name : ''; };
  const getCustDist = (id) => { const c = customerMap[id]; return c?.distributor_id ? distributors.find(d => d.id === c.distributor_id)?.company_name : ''; };

  const stateOptions = [...new Set(customers.map(c => c.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const cityOptions = [...new Set(customers.filter(c => !filterStates.length || filterStates.includes(c.state)).map(c => c.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const agentOptions = agents.filter(a => a.is_active).map(a => ({ value: a.id, label: a.name }));
  const distributorOptions = distributors.filter(d => d.is_active).map(d => ({ value: d.id, label: d.company_name }));
  const staffOptions = [...new Set(returns.map(r => r.created_by_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }));

  const filteredReturns = returns.filter(r => {
    if (searchTerm && !(customers.find(c => c.id === r.customer_id)?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) &&
        !(/\d/.test(searchTerm) && (r.return_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.credit_note_number?.toLowerCase().includes(searchTerm.toLowerCase()))) &&
        !(r.items || []).some(item => item.design_code?.toLowerCase().includes(searchTerm.toLowerCase()))) return false;
    if (filterCustomer && r.customer_id !== filterCustomer) return false;
    if (filterDateFrom && r.return_date < filterDateFrom) return false;
    if (filterDateTo && r.return_date > filterDateTo) return false;
    const cust = customerMap[r.customer_id];
    if (filterStates.length && (!cust || !filterStates.includes(cust.state))) return false;
    if (filterCities.length && (!cust || !filterCities.includes(cust.city))) return false;
    if (filterAgents.length && (!cust || !filterAgents.includes(cust.agent_id))) return false;
    if (filterDistributors.length && (!cust || !filterDistributors.includes(cust.distributor_id))) return false;
    if (filterStaff.length && !filterStaff.includes(r.created_by_name)) return false;
    return true;
  });
  const hasActiveFilters = !!(searchTerm || filterCustomer || filterDateFrom || filterDateTo || filterStates.length || filterCities.length || filterAgents.length || filterDistributors.length || filterStaff.length);

  const deleteReturn = async (id, num) => {
    try {
      await api.delete(`/sales-returns/${id}`);
      toast.success(`${num} deleted. Stock deducted again.`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`;
  };
  const reasonLabel = (r) => RETURN_REASONS.find(x => x.value === r)?.label || r;
  const reasonColor = (r) => {
    if (r === 'defective' || r === 'damaged') return 'text-red-700 bg-red-50';
    if (r === 'quality_issue') return 'text-orange-700 bg-orange-50';
    if (r === 'excess') return 'text-blue-700 bg-blue-50';
    if (r === 'wrong_item') return 'text-purple-700 bg-purple-50';
    if (r === 'not_moving') return 'text-amber-700 bg-amber-50';
    return 'text-gray-700 bg-gray-50';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600" /></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="sales-return-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Sales Returns</h1>
          <p className="text-gray-600 mt-1">Process customer returns, issue credit notes, restore stock</p>
        </div>
      </div>

      <Card data-testid="sr-filters">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Search Return # / Credit Note</Label>
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Return #, Credit Note, Customer or Design No..." className="h-9" data-testid="sr-filter-search" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Customer</Label>
              <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="sr-filter-customer">
                <option value="">All Customers</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Date From</Label>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="h-9" data-testid="sr-filter-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Date To</Label>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="h-9" data-testid="sr-filter-date-to" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <MultiSelectFilter label="State" options={stateOptions} selected={filterStates} onChange={setFilterStates} testId="sr-filter-state" />
            <MultiSelectFilter label="City" options={cityOptions} selected={filterCities} onChange={setFilterCities} testId="sr-filter-city" />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={filterAgents} onChange={setFilterAgents} testId="sr-filter-agent" />
            <MultiSelectFilter label="Distributor" options={distributorOptions} selected={filterDistributors} onChange={setFilterDistributors} testId="sr-filter-distributor" />
            <MultiSelectFilter label="Staff" options={staffOptions} selected={filterStaff} onChange={setFilterStaff} testId="sr-filter-staff" />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500" onClick={() => { setSearchTerm(''); setFilterCustomer(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterStates([]); setFilterCities([]); setFilterAgents([]); setFilterDistributors([]); setFilterStaff([]); }} data-testid="sr-clear-filters">
                Clear All
              </Button>
            )}
          </div>
          {hasActiveFilters && <p className="text-xs text-gray-500">Showing {filteredReturns.length} of {returns.length} returns</p>}
        </CardContent>
      </Card>

      <Card data-testid="sr-list">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <CardTitle>Sales Returns ({filteredReturns.length})</CardTitle>
            {canCreate && (
              <Button size="sm" onClick={() => navigate('/create-sales-return')} className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-sm" data-testid="create-sr-button">
                + New Sales Return
              </Button>
            )}
          </div>
          <CardDescription>Customer returns with credit notes and stock restoration</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredReturns.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{hasActiveFilters ? 'No matching returns' : 'No Sales Returns Yet'}</h3>
              <p className="text-gray-600 mb-4">{hasActiveFilters ? 'Try adjusting your filters.' : 'Process a return when a customer sends goods back.'}</p>
              {!hasActiveFilters && canCreate && <Button onClick={() => navigate('/create-sales-return')} className="bg-orange-600">Create First Return</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sr-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll(filteredReturns.map(x => x.id)); else bulk.clearSelection(); }} /></th>
                    <th className="px-4 py-3 text-left font-medium">Return #</th>
                    <th className="px-4 py-3 text-left font-medium">Credit Note</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-left font-medium">Customer</th>
                    <th className="px-4 py-3 text-center font-medium">Items</th>
                    <th className="px-4 py-3 text-right font-medium">Qty</th>
                    <th className="px-4 py-3 text-right font-medium">Value</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Reasons</th>
                    <th className="px-4 py-3 text-left font-medium">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredReturns.map(ret => {
                    const reasons = new Set();
                    (ret.items || []).forEach(i => i.variants?.forEach(v => reasons.add(v.reason)));
                    return (
                      <tr key={ret.id} className={`hover:bg-orange-50/50 cursor-pointer transition-colors group ${bulk.isSelected(ret.id) ? "bg-blue-50" : ""}`} data-testid={`sr-row-${ret.id}`} onClick={() => navigate(`/sales-return/${ret.id}`)}>
                      <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(ret.id)} onChange={() => bulk.toggleSelect(ret.id)} /></td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-orange-700 font-mono group-hover:underline">{ret.return_number}</span>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-orange-700" data-testid={`sr-cn-link-${ret.id}`}>{ret.credit_note_number || <span className="text-gray-400">-</span>}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(ret.return_date)}
                          {ret.created_at && <span className="block text-[10px] text-gray-400">{new Date(ret.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px]">
                          <div className="truncate">{getCustDisplay(ret.customer_id)}</div>
                          {ret.event_name && (
                            <span
                              className="inline-block mt-1 text-[10px] font-medium bg-pink-50 text-pink-700 border border-pink-200 px-1.5 py-0.5 rounded truncate max-w-full"
                              title={ret.event_name}
                              data-testid={`sr-row-event-${ret.id}`}
                            >
                              🎟 {ret.event_name}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{ret.items?.length || 0}</td>
                        <td className="px-4 py-3 text-right font-medium text-orange-700">{ret.total_qty}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">{(ret.total_value || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          {canChangeStatus ? (
                            <select
                              value={ret.tally_status || 'pending'}
                              onChange={e => handleStatusChange(ret.id, e.target.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-md border cursor-pointer ${(ret.tally_status || 'pending') === 'billed' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-yellow-100 text-yellow-700 border-yellow-300'}`}
                              data-testid={`sr-status-${ret.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="billed">Billed</option>
                            </select>
                          ) : (
                            <Badge className={`text-xs ${(ret.tally_status || 'pending') === 'billed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {(ret.tally_status || 'pending') === 'billed' ? 'Billed' : 'Pending'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {[...reasons].map(r => (
                              <span key={r} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${reasonColor(r)}`}>{reasonLabel(r)}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">{ret.created_by_name || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credit Note Number Dialog */}
      <Dialog open={!!billingDialog} onOpenChange={v => { if (!v) setBillingDialog(null); }}>
        <DialogContent className="max-w-sm z-50">
          <DialogHeader>
            <DialogTitle>Enter Credit Note Number</DialogTitle>
            <DialogDescription>Optional: Enter the credit note number for this sales return</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Credit Note Number</Label>
              <Input
                value={billingDialog?.inputValue || ''}
                onChange={e => setBillingDialog(prev => ({ ...prev, inputValue: e.target.value }))}
                placeholder="e.g. CN-001"
                autoFocus
                data-testid="billing-credit-note-input"
                onKeyDown={e => { if (e.key === 'Enter') confirmBilling(); }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setBillingDialog(null)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={confirmBilling} data-testid="billing-confirm-btn">Mark as Billed</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="SR" label="sales returns" onComplete={() => { bulk.clearSelection(); fetchReturns(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default SalesReturnManagement;
